import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { Save, X, ArrowLeft, Maximize2, Minimize2, Folder } from 'lucide-react'
import { Link } from 'react-router-dom'
import { notesAPI } from '../../services/api'
import sensitiveNotesService from '../../services/sensitiveNotesService'
import { useTabs } from '../../contexts/TabsContext'
import { useNotes } from '../../contexts/NotesContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useSensitiveSession } from '../../contexts/SensitiveSessionContext'
import { ButtonSpinner } from '../../components/common/LoadingSpinner'
import SensitiveNotePasswordModal from '../../components/folders/SensitiveNotePasswordModal'
import RichTextEditor from '../../components/common/RichTextEditor'
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts'
import toast from 'react-hot-toast'

/**
 * Create Note Page Component
 * 
 * Form for creating new notes with title and content.
 * Includes form validation, API integration, and error handling.
 * Can work in both standalone mode and tab mode.
 * Supports both cloud and local storage modes.
 */
const CreateNotePage = ({ tabId = null }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { tabs, openTab, closeTab, updateTab } = useTabs()
  const { fetchNotes, createNote } = useNotes()
  const { fetchFolders, currentFolder } = useFolders()
  const { isLocalMode } = useLocalMode()
  const sensitiveSession = useSensitiveSession()
  const isInTab = tabId !== null
  const [isLoading, setIsLoading] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [pendingNoteData, setPendingNoteData] = useState(null)
  
  // Refs for form field focus management
  const titleInputRef = useRef(null)
  const contentEditorRef = useRef(null)
  const richTextEditorRef = useRef(null)

  // Get current tab data to restore form state
  const currentTab = tabs.find(tab => tab.id === tabId)
  
  // Check for imported note data first, then fall back to formData
  const importedNote = currentTab?.note
  const savedFormData = currentTab?.formData || { title: '', content: '' }
  
  // Use imported note data if available, otherwise use saved form data
  const initialFormData = importedNote ? {
    title: importedNote.title || '',
    content: importedNote.content || ''
  } : savedFormData
  
  // Initialize isExpanded based on tab property or navigation state
  const [isExpanded, setIsExpanded] = useState(() => {
    if (isInTab && currentTab?.isExpanded) {
      return true // Tab has isExpanded property set
    }
    if (!isInTab && location.state?.isExpanded) {
      return true // Navigation state has isExpanded
    }
    return false // Default to not expanded
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    control,
    setValue,
    reset
  } = useForm({
    defaultValues: initialFormData
  })

  // Watch form values for real-time updates
  const watchedTitle = watch('title')
  const watchedContent = watch('content')

  /**
   * Check if form has changes for unsaved warning
   */
  const hasChanges = () => {
    return watchedTitle?.trim() || watchedContent?.trim()
  }

  // Update tab dirty state when form changes
  useEffect(() => {
    if (isInTab && tabId) {
      const isDirty = hasChanges()
      updateTab(tabId, { isDirty })
    }
  }, [watchedTitle, watchedContent, isInTab, tabId])

  // Update tab title dynamically as user types
  useEffect(() => {
    if (isInTab && tabId) {
      const title = watchedTitle?.trim() || 'New Note'
      updateTab(tabId, { title })
    }
  }, [watchedTitle, isInTab, tabId])

  // Restore form data when tab becomes active
  useEffect(() => {
    if (isInTab && tabId && savedFormData) {
      reset(savedFormData)
    }
  }, [tabId, isInTab, reset])

  // Handle imported note data
  useEffect(() => {
    if (isInTab && tabId && importedNote) {
      console.log('CreateNotePage: Loading imported note data:', importedNote)
      reset({
        title: importedNote.title || '',
        content: importedNote.content || ''
      })
    }
  }, [isInTab, tabId, importedNote, reset])

  // Save form data to tab whenever user types
  useEffect(() => {
    if (isInTab && tabId && (watchedTitle !== undefined || watchedContent !== undefined)) {
      const formData = {
        title: watchedTitle || '',
        content: watchedContent || ''
      }
      updateTab(tabId, { formData })
    }
  }, [watchedTitle, watchedContent, isInTab, tabId])

  /**
   * Handle form submission to create a new note
   */
  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      clearErrors()

      // Validate that at least one field has content
      if (!data.title?.trim() && !data.content?.trim()) {
        setError('root', { message: 'Please provide either a title or content for your note.' })
        return
      }

      // Process content to replace base64 images with Google Drive URLs
      let processedContent = data.content
      if (richTextEditorRef.current && richTextEditorRef.current.processContentForSave) {
        processedContent = richTextEditorRef.current.processContentForSave(data.content)
        console.log('🔄 CreateNotePage: Processed content for save:', processedContent !== data.content ? 'YES' : 'NO')
      }

      // Include current folder if one is selected, but create in All Notes if in sensitive folder
      const noteData = {
        ...data,
        content: processedContent,
        folderId: currentFolder?.folderType === 'SENSITIVE' ? null : (currentFolder?.folderId || null)
      }

      // Check if creating in sensitive folder (only in cloud mode) - this should not happen now
      if (!isLocalMode && currentFolder?.folderType === 'SENSITIVE' && noteData.folderId) {
        console.log('🔐 CreateNotePage: Creating note in sensitive folder, requesting password')
        setPendingNoteData(noteData)
        setShowPasswordModal(true)
        setIsLoading(false)
        return
      }

      // Use unified createNote (handles both local and cloud modes)
      console.log('🔍 CreateNotePage: Submitting note data:', noteData)
      const response = await createNote(noteData)
      console.log('✅ CreateNotePage: Note created successfully:', response)

      // Skip background refresh for now - the new note is already cached
      // Background sync would be redundant and might timeout
      if (!isLocalMode) {
        // Just refresh folders for updated counts, skip notes sync
        fetchFolders().catch(error => {
          console.warn('⚠️ CreateNotePage: Background folder refresh failed (non-blocking):', error)
        })
      }

      // Success message is handled by the createNote function
      
      if (response && isInTab && tabId) {
        // Open the new note in view mode and close create tab
        openTab({
          type: 'view',
          noteId: response.noteId,
          title: data.title || 'Untitled Note',
          note: response
        })
        closeTab(tabId)
      } else {
        navigate('/notes')
      }
    } catch (error) {
      console.error('❌ CreateNotePage: Error creating note:', error)
      
      // In local mode, we don't expect authentication errors
      if (isLocalMode) {
        setError('root', { message: 'Failed to create note locally. Please try again.' })
        return
      }
      
      // Handle server errors (cloud mode only)
      if (error.response?.status === 400) {
        const errorData = error.response.data
        if (errorData.field) {
          setError(errorData.field, { message: errorData.message })
        } else {
          setError('root', { message: errorData.message || 'Failed to create note. Please check your input.' })
        }
      } else if (error.response?.status === 401) {
        toast.error('Please log in to create notes')
        navigate('/login')
      } else {
        setError('root', { message: 'Failed to create note. Please try again.' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Listen for mobile save trigger from header
  useEffect(() => {
    const handleMobileSave = () => {
      handleSubmit(onSubmit)()
    }

    window.addEventListener('mobile-save-trigger', handleMobileSave)
    return () => window.removeEventListener('mobile-save-trigger', handleMobileSave)
  }, [handleSubmit, onSubmit])

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'ctrl+shift+s': (event) => {
      console.log('🎹 Keyboard shortcut: Ctrl+Shift+S - Saving note')
      handleSubmit(onSubmit)()
      toast('💾 Saving note...', { duration: 1500 })
    }
  }, [handleSubmit, onSubmit])

  // Auto-focus on title field when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (titleInputRef.current) {
        titleInputRef.current.focus()
      }
    }, 100) // Small delay to ensure DOM is ready

    return () => clearTimeout(timer)
  }, []) // Only run once on mount

  /**
   * Handle Tab key navigation from title to content
   */
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault()
      // Focus the content editor
      if (contentEditorRef.current) {
        // For RichTextEditor, we need to focus the internal editor
        const editorElement = contentEditorRef.current.querySelector('[contenteditable="true"], textarea, .ql-editor')
        if (editorElement) {
          editorElement.focus()
        }
      }
    }
  }

  /**
   * Handle password confirmation for sensitive folder note creation
   */
  const handlePasswordConfirm = async (password) => {
    try {
      setIsLoading(true)
      
      console.log('🔐 CreateNotePage: Creating encrypted note with frontend encryption')
      const response = await sensitiveNotesService.createNote(pendingNoteData, password)
      console.log('✅ CreateNotePage: Encrypted note created successfully:', response)

      // Refresh notes list and folder counts
      await Promise.all([
        fetchNotes(),
        fetchFolders() // Update folder note counts
      ])

      toast.success('Secure note created!')
      
      if (isInTab && tabId) {
        // Open the new note in view mode and close create tab
        openTab({
          type: 'view',
          noteId: response.noteId,
          title: pendingNoteData.title || 'Untitled Note',
          note: response
        })
        closeTab(tabId)
      } else {
        navigate('/notes')
      }
    } catch (error) {
      console.error('❌ CreateNotePage: Error creating encrypted note:', error)
      
      if (error.response?.data?.includes?.('Password required')) {
        toast.error('Password required for sensitive folder operations')
      } else if (error.response?.data?.includes?.('Invalid password')) {
        toast.error('Invalid password - note creation failed')
      } else {
        toast.error('Failed to create encrypted note. Please try again.')
      }
    } finally {
      setIsLoading(false)
      setShowPasswordModal(false)
      setPendingNoteData(null)
    }
  }

  /**
   * Handle password modal cancellation
   */
  const handlePasswordCancel = () => {
    setShowPasswordModal(false)
    setPendingNoteData(null)
    setIsLoading(false)
  }

  /**
   * Handle expand/restore toggle
   */
  const handleExpandToggle = (e) => {
    e.preventDefault() // Prevent form submission
    e.stopPropagation() // Stop event bubbling
    setIsExpanded(!isExpanded)
  }

  /**
   * Handle navigation away with unsaved changes
   */
  const handleNavigateAway = (e) => {
    if (hasChanges() && !isLoading) {
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
      )
      if (!confirmLeave) {
        e.preventDefault()
        return false
      }
    }
    return true
  }

  // Add beforeunload event listener for browser navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges() && !isLoading) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [watchedTitle, watchedContent, isLoading])

  return (
    <div className="space-y-2">

      {/* Create Note Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className={`card ${isExpanded ? 'note-fullscreen' : ''}`}>
          <div className="card-body space-y-2 relative">
                        {isExpanded ? (
              /* NEW: Floating Controls Fullscreen Layout */
              <>
                {/* Floating Action Controls */}
                <div className="floating-controls">
                  {/* Create Button */}
                  <button
                    type="submit"
                    disabled={isLoading || !hasChanges()}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 rounded-lg transition-colors shadow-lg"
                    title="Create note"
                  >
                    {isLoading ? (
                      <ButtonSpinner size="sm" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                  </button>

                  {/* Cancel Button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (isInTab) {
                        closeTab(tabId)
                      } else {
                        navigate('/notes')
                      }
                    }}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors shadow-lg"
                    title="Cancel creation"
                  >
                    <X className="h-4 w-4" />
                  </button>

                  {/* Exit Fullscreen Button */}
                  <button
                    type="button"
                    onClick={handleExpandToggle}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors shadow-lg"
                    title="Exit fullscreen"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Floating Title Field */}
                <div className="floating-title">
                  <input
                    {...register('title', {
                      required: 'Note title is required',
                      maxLength: {
                        value: 255,
                        message: 'Title must be less than 255 characters'
                      }
                    })}
                    ref={(e) => {
                      register('title').ref(e) // react-hook-form ref
                      titleInputRef.current = e // our custom ref
                    }}
                    type="text"
                    id="title"
                    placeholder="Enter note title..."
                    disabled={isLoading}
                    onKeyDown={handleTitleKeyDown}
                  />
                </div>

                {/* Fullscreen Editor */}
                <div className="fullscreen-editor" ref={contentEditorRef}>
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        ref={richTextEditorRef}
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Start writing your note..."
                        disabled={isLoading}
                        className="rich-text-editor"
                        isFullscreen={true}
                      />
                    )}
                  />
                </div>

                {/* Error Messages (floating above bottom toolbar) */}
                {(errors.root || errors.title || errors.content) && (
                  <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-1100 max-w-md">
                    {errors.root && (
                      <div className="bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-2 backdrop-blur-lg">
                        <div className="text-sm text-red-700 dark:text-red-300">
                          {errors.root.message}
                        </div>
                      </div>
                    )}
                    {errors.title && (
                      <div className="bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-2 backdrop-blur-lg">
                        <div className="text-sm text-red-700 dark:text-red-300">
                          {errors.title.message}
                        </div>
                      </div>
                    )}
                    {errors.content && (
                      <div className="bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-800 rounded-lg p-3 backdrop-blur-lg">
                        <div className="text-sm text-red-700 dark:text-red-300">
                          {errors.content.message}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Normal Layout */
              <>
                {/* Expand/Restore Button */}
                <div className="expand-button">
                  {/* HIDDEN: Expand to full screen button - commented out for now
                  <button
                    type="button"
                    onClick={handleExpandToggle}
                    className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 shadow-sm transition-colors duration-200"
                    title="Expand to full screen"
                  >
                    <Maximize2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  */}
                </div>

                {/* Form Errors */}
                {errors.root && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="text-sm text-red-700 dark:text-red-300">
                      {errors.root.message}
                    </div>
                  </div>
                )}

                {/* Title Field with Action Buttons */}
                <div>
                  <div className="flex gap-3 items-start">
                    {/* Title Input - 60% width */}
                    <div className="flex-1" style={{ width: '60%' }}>
                      <input
                        {...register('title', {
                          required: 'Note title is required',
                          maxLength: {
                            value: 255,
                            message: 'Title must be less than 255 characters'
                          }
                        })}
                        ref={(e) => {
                          register('title').ref(e) // react-hook-form ref
                          titleInputRef.current = e // our custom ref
                        }}
                        type="text"
                        id="title"
                        className={`form-input ${errors.title ? 'error' : ''}`}
                        placeholder="Enter note title..."
                        disabled={isLoading}
                        onKeyDown={handleTitleKeyDown}
                      />
                      {errors.title && (
                        <p className="form-error">{errors.title.message}</p>
                      )}
                      <p className="form-help">
                        {watchedTitle?.length || 0}/255 characters
                        {currentFolder?.folderType === 'SENSITIVE' ? (
                          <span className="ml-2 text-orange-600 dark:text-orange-400">
                            • Creating in All Notes (not in {currentFolder.name})
                          </span>
                        ) : currentFolder ? (
                          <span className="ml-2 text-blue-600 dark:text-blue-400">
                            • Creating in {currentFolder.name}
                          </span>
                        ) : (
                          <span className="ml-2 text-gray-600 dark:text-gray-400">
                            • Creating in All Notes
                          </span>
                        )}
                      </p>
                    </div>
                    
                    {/* Action Buttons - 40% width divided into 2 parts */}
                    <div className="flex gap-2" style={{ width: '40%' }}>
                      {/* Save Button - 1/2 of 40% */}
                      <div className="flex-1">
                        <button
                          onClick={handleSubmit(onSubmit)}
                          disabled={isLoading || !hasChanges()}
                          className="w-full btn-compact-primary"
                          title="Create note"
                        >
                          {isLoading ? (
                            <ButtonSpinner size="sm" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      
                      {/* Close Button - 1/2 of 40% */}
                      <div className="flex-1">
                        {isInTab ? (
                          <button
                            onClick={() => {
                              if (handleNavigateAway({})) {
                                closeTab(tabId)
                              }
                            }}
                            className="w-full btn-compact text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="Close tab"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : (
                          <Link
                            to="/notes"
                            onClick={handleNavigateAway}
                            className="w-full btn-compact text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="Back to notes"
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content Field */}
                <div>
                  
                  <div ref={contentEditorRef}>
                    <Controller
                      name="content"
                      control={control}
                      render={({ field }) => (
                        <RichTextEditor
                          ref={richTextEditorRef}
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="Start writing your note..."
                          disabled={isLoading}
                          className={`rich-text-editor ${errors.content ? 'error' : ''}`}
                          isFullscreen={false}
                        />
                      )}
                    />
                  </div>
                  {errors.content && (
                    <p className="form-error">{errors.content.message}</p>
                  )}
                  <p className="form-help">
                    
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

      </form>

      {/* Sensitive Note Password Modal */}
      <SensitiveNotePasswordModal
        isOpen={showPasswordModal}
        onClose={handlePasswordCancel}
        onConfirm={handlePasswordConfirm}
        operationType="encrypt"
        title={pendingNoteData?.title || 'New Note'}
      />
    </div>
  )
}

export default CreateNotePage 